﻿import { Button, DropDownButton, DropDownButtonItem } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar, Window } from '@progress/kendo-react-dialogs';
import * as turf from '@turf/turf';
import React, { useEffect, useRef } from 'react';
import { LoadingIndicatorCircular } from '../../app/components/indicators/LoadingIndicatorCircular';
import { EventBus } from '../../app/contexts/event-bus';
import { useAppDispatch } from '../../app/hooks/store-hooks';
import { useFlightWorker } from '../../app/hooks/use-flight-worker';
import useMap from '../../app/hooks/use-map';
import { toggleLeftFlyout } from '../../app/state/slices/workspace-slice';
import Coordinate from '../../common/coordinate';
import { DateTimeFormatter, UnitFormatter } from '../../formatters';
import useIntegration from '../../hooks/use-integration';
import {
    AirportTowerOutline,
    CheckmarkOutline,
    ChevronDownOutline,
    ChevronUpOutline,
    DataInsightsOutline,
    DrawingPolygonOutline,
    DuplicateOutline,
    FlightListOutline,
    FlightListSolid,
    InfoCircleOutline,
    MapOutline,
    MenuOverflowVerticalOutline
} from '../../icons/icons';
import { FAIResponse, MessageType } from '../../services/actor-messages';
import useConstraintsStore from '../../store/use-constraints-store';
import { FPMFillColor, FPMLineColor } from '../drawing-object/DrawingObjectEditWindow';
import { DrawingObjectCategory, DrawingObjectFillStyle, DrawingObjectType } from '../drawing-object/types';
import { AnalyzerRouteType, MinimalAnalyzerRoute } from '../route-analyzer/types';
import { AirportDelay, FlightConstrainedArea, PolygonFlightIntersection, RerouteAdvisory, ShapeType } from './types';

export interface ConstraintListProps {
    onClose?: () => void;
}

interface DetailsProps {
    title: string;
    header: string;
    content: string;
    showVerboseButton: boolean;
    verboseContent?: string;
}

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
    column: string | null;
    direction: SortDirection;
}

interface SortableHeaderProps {
    children: React.ReactNode;
    column: string;
    sortState: SortState;
    onSort: (column: string) => void;
}

function SortableHeader({ children, column, sortState, onSort }: SortableHeaderProps) {
    const isActive = sortState.column === column;
    const direction = isActive ? sortState.direction : null;

    return (
        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(column)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{children}</span>
                <span style={{ marginLeft: '4px', opacity: isActive ? 1 : 0.3 }}>
                    {direction === 'asc' && <SvgIcon icon={ChevronUpOutline} style={{ height: 12, width: 12 }} />}
                    {direction === 'desc' && <SvgIcon icon={ChevronDownOutline} style={{ height: 12, width: 12 }} />}
                </span>
            </div>
        </th>
    );
}

const ConstraintsList = (props: ConstraintListProps) => {
    const map = useMap();
    const dispatch = useAppDispatch();
    const [intersectionName, setIntersectionName] = React.useState<string | null>(null);
    const [intersectionComplete, setIntersectionComplete] = React.useState(false);
    const [isVerbose, setIsVerbose] = React.useState(false);
    const [isCopied, setIsCopied] = React.useState(false);
    const [details, setDetails] = React.useState<DetailsProps | null>(null);
    const [intersectingFlights, setIntersectingFlights] = React.useState<PolygonFlightIntersection[]>([]);
    const [previousIntersectionSequence, setPreviousIntersectionSequence] = React.useState<number>(0);

    // Sort states for each table
    const [rerouteSort, setRerouteSort] = React.useState<SortState>({ column: null, direction: null });
    const [airportSort, setAirportSort] = React.useState<SortState>({ column: null, direction: null });
    const [fcaSort, setFcaSort] = React.useState<SortState>({ column: null, direction: null });
    const [intersectionSort, setIntersectionSort] = React.useState<SortState>({ column: null, direction: null });

    const currentSequenceRef = useRef(0);
    const airportDrawingObjectRadius = 5;
    const integrationInfo = useIntegration();
    const worker = useFlightWorker();

    const _map = useMap();
    const {
        rerouteAdvisories,
        airportDelays,
        flightConstrainedAreas,
        lastRerouteUpdate,
        lastAirportDelayUpdate,
        lastFlightConstrainedAreaUpdate,
        fetchConstraints
    } = useConstraintsStore();

    // Set up polling while dialog is open
    useEffect(() => {
        let interval: number | null = null;

        fetchConstraints();

        interval = self.setInterval(() => {
            fetchConstraints();
        }, 30000);

        return () => {
            if (interval) {
                self.clearInterval(interval);
            }
        };
    }, [fetchConstraints]);

    useEffect(() => {
        const handleFAIResponse = (message: FAIResponse) => {
            if (message.sequence !== currentSequenceRef.current && message.sequence !== previousIntersectionSequence) {
                setIntersectionSequence(message.sequence);
            }
            if (currentSequenceRef.current === message.sequence) {
                setIntersectingFlights((flights) => [...flights, ...message.flights]);
            }
            if (message.sequence === currentSequenceRef.current && message.complete) {
                setIntersectionComplete(true);
            }
        };

        EventBus.on('FAIResponse', handleFAIResponse);

        return () => {
            EventBus.off('FAIResponse', handleFAIResponse);
        };
    }, []);

    const setIntersectionSequence = (sequence: number) => {
        if (currentSequenceRef.current !== sequence) {
            setPreviousIntersectionSequence(currentSequenceRef.current);
            currentSequenceRef.current = sequence;
            setIntersectingFlights([]);
        }
    };

    const handleAnalyzeRoutes = (x: RerouteAdvisory) => {
        const routes: MinimalAnalyzerRoute[] = x.routes.map((route) => ({
            label: x.reRouteName + '(' + route.sequence + ')',
            route: route.route,
            origin: '',
            destination: '',
            distance: route.distance,
            sequence: String(route.sequence) || null,
            type: AnalyzerRouteType.reroute
        }));
        EventBus.fire('route-analyzer-load-routes', routes);
    };

    const saveToClipboard = () => {
        if (details) {
            navigator.clipboard
                .writeText(isVerbose && details.verboseContent ? details.verboseContent : details.content)
                .then(() => {
                    setIsCopied(true);
                    self.setTimeout(() => setIsCopied(false), 2000);
                })
                .catch((err) => {
                    console.error('Failed to copy details: ', err);
                });
        }
    };

    const addDrawingObject = (x: FlightConstrainedArea) => {
        const base = {
            name: x.name,
            label: {
                text: x.name
            }
        };
        openDrawingObjectEditProperties(x, base);
    };

    const addFPMAvoidanceArea = (x: FlightConstrainedArea) => {
        const base = {
            name: `Avoidance_${x.name}`,
            label: {
                text: `Avoidance_${x.name}`
            },
            category: DrawingObjectCategory.avoidance,
            fpmProperties: {
                sync: true
            },
            line: {
                color: FPMLineColor
            },
            fill: {
                color: FPMFillColor,
                style: DrawingObjectFillStyle.diagHatchLeft
            }
        };
        openDrawingObjectEditProperties(x, base);
    };

    const openDrawingObjectEditProperties = (x: FlightConstrainedArea, base: any) => {
        switch (x.shape) {
            case ShapeType.Poly:
                EventBus.fire('draw.create', {
                    drawObject: {
                        ...base,
                        type: DrawingObjectType.polygon,
                        points: [...x.points, x.points[0]] //close the polygon
                    }
                });
                break;
            case ShapeType.Circular:
                EventBus.fire('draw.create', {
                    drawObject: {
                        ...base,
                        type: DrawingObjectType.circle,
                        center: x.center,
                        radius: x.radius ? [x.radius, x.radius] : [0, 0]
                    }
                });
                break;
            case ShapeType.Airport:
                EventBus.fire('draw.create', {
                    drawObject: {
                        ...base,
                        type: DrawingObjectType.circle,
                        center: x.center,
                        radius: airportDrawingObjectRadius ? [airportDrawingObjectRadius, airportDrawingObjectRadius] : [0, 0]
                    }
                });
                break;
            case ShapeType.Line:
                EventBus.fire('draw.create', {
                    drawObject: {
                        ...base,
                        type: DrawingObjectType.polygon,
                        points: getIntersectionPolygon(x) // convert line to a very thin polygon
                    }
                });
                break;
        }
    };

    const getIntersectionPolygon = (x: FlightConstrainedArea) => {
        if (x.shape === ShapeType.Poly) {
            return [...x.points, x.points[0]];
        }
        if (x.shape === ShapeType.Line) {
            // create a thin polygon buffer around the line
            const buffer = 0.00001; // small offset
            const polyPoints = [];

            // First side of the buffer
            for (let i = 0; i < x.points.length; i++) {
                const point = x.points[i];
                // Calculate perpendicular offset
                if (i < x.points.length - 1) {
                    const nextPoint = x.points[i + 1];
                    const dx = nextPoint[0] - point[0];
                    const dy = nextPoint[1] - point[1];
                    const len = Math.sqrt(dx * dx + dy * dy);
                    polyPoints.push([point[0] - (dy / len) * buffer, point[1] + (dx / len) * buffer]);
                } else {
                    // Last point uses same offset direction as previous segment
                    const prevPoint = x.points[i - 1];
                    const dx = point[0] - prevPoint[0];
                    const dy = point[1] - prevPoint[1];
                    const len = Math.sqrt(dx * dx + dy * dy);
                    polyPoints.push([point[0] - (dy / len) * buffer, point[1] + (dx / len) * buffer]);
                }
            }

            // Second side of the buffer (in reverse)
            for (let i = x.points.length - 1; i >= 0; i--) {
                const point = x.points[i];
                if (i > 0) {
                    const prevPoint = x.points[i - 1];
                    const dx = point[0] - prevPoint[0];
                    const dy = point[1] - prevPoint[1];
                    const len = Math.sqrt(dx * dx + dy * dy);
                    polyPoints.push([point[0] + (dy / len) * buffer, point[1] - (dx / len) * buffer]);
                } else {
                    // Close the polygon by connecting back to the first point
                    polyPoints.push(polyPoints[0]);
                }
            }

            return polyPoints;
        }
        const circlePolygon = turf.circle(x.center, x.radius || airportDrawingObjectRadius, {
            steps: 5,
            units: 'nauticalmiles'
        });
        return circlePolygon.geometry.coordinates[0];
    };

    const openRerouteDetails = (x: RerouteAdvisory) => {
        setDetails({
            title: 'Advisory details',
            header: `Id: ${x.reRouteName}`,
            content: x.details?.length ? x.details : 'No details available.',
            showVerboseButton: false
        });
    };
    const openAirportDetails = (x: AirportDelay) => {
        setDetails({
            title: 'FAA delay details',
            header: `${x.name} - ${x.fullName}`,
            content: x.details?.length ? x.details : 'No details available.',
            verboseContent: x.verboseDetails?.length ? x.verboseDetails : 'No verbose details available.',
            showVerboseButton: true
        });
    };
    const openFCADetails = (x: FlightConstrainedArea) => {
        setDetails({
            title: 'FCA/FEA/AFP details',
            header: `Id: ${x.name}`,
            content: x.details?.length ? x.details : 'No details available.',
            showVerboseButton: false
        });
    };

    const showIntersectingFlights = (x: FlightConstrainedArea, myFleetOnly: boolean = false) => {
        setIntersectionName(x.name);
        setIntersectionComplete(false);
        setIntersectingFlights([]);
        worker.actor.sendAsync({
            type: myFleetOnly ? MessageType.requestMyFleetIntersectingFlights : MessageType.requestAllIntersectingFlights,
            data: getIntersectionPolygon(x)
        });
    };

    // Sort handling functions
    const handleSort = (column: string, currentSort: SortState, setSortState: React.Dispatch<React.SetStateAction<SortState>>) => {
        let newDirection: SortDirection = 'asc';

        if (currentSort.column === column) {
            if (currentSort.direction === 'asc') {
                newDirection = 'desc';
            } else if (currentSort.direction === 'desc') {
                newDirection = null;
            }
        }

        setSortState({ column: newDirection ? column : null, direction: newDirection });
    };

    // Generic sort function
    const sortData = <T,>(data: T[], sortState: SortState, getValue: (item: T, column: string) => any): T[] => {
        if (!sortState.column || !sortState.direction) {
            return data;
        }

        return [...data].sort((a, b) => {
            const aVal = getValue(a, sortState.column!);
            const bVal = getValue(b, sortState.column!);

            if (aVal === null || aVal === undefined) {
                return 1;
            }
            if (bVal === null || bVal === undefined) {
                return -1;
            }

            let comparison: number;
            if (typeof aVal === 'string' && typeof bVal === 'string') {
                comparison = aVal.localeCompare(bVal);
            } else if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal - bVal;
            } else if (aVal instanceof Date && bVal instanceof Date) {
                comparison = aVal.getTime() - bVal.getTime();
            } else {
                comparison = String(aVal).localeCompare(String(bVal));
            }

            return sortState.direction === 'asc' ? comparison : -comparison;
        });
    };

    // Data getters for sorting
    const getRerouteValue = (item: RerouteAdvisory, column: string) => {
        switch (column) {
            case 'advisoryNumber':
                return item.advisoryNumber;
            case 'type':
                return item.type;
            case 'category':
                return item.category;
            case 'action':
                return item.action;
            case 'status':
                return item.status;
            case 'method':
                return item.method;
            case 'conformantFlights':
                return item.conformantFlights;
            case 'nonConformantFlights':
                return item.nonConformantFlights;
            case 'affectedRoutes':
                return item.affectedRoutes;
            case 'reRouteName':
                return item.reRouteName;
            default:
                return '';
        }
    };

    const getAirportValue = (item: AirportDelay, column: string) => {
        switch (column) {
            case 'name':
                return item.name;
            case 'type':
                return item.type;
            case 'reason':
                return item.reason;
            case 'start':
                return item.start;
            case 'end':
                return item.end;
            default:
                return '';
        }
    };

    const getFcaValue = (item: FlightConstrainedArea, column: string) => {
        switch (column) {
            case 'name':
                return item.name;
            case 'type':
                return item.type;
            case 'reason':
                return item.reason;
            case 'start':
                return item.start;
            case 'end':
                return item.end;
            case 'altitude':
                return item.altitude;
            default:
                return '';
        }
    };

    const getIntersectionValue = (item: PolygonFlightIntersection, column: string) => {
        switch (column) {
            case 'acid':
                return item.acid;
            case 'origin':
                return item.origin;
            case 'destination':
                return item.destination;
            case 'status':
                return item.status;
            case 'departureTime':
                return item.departureTime;
            case 'arrivalTime':
                return item.arrivalTime;
            case 'entryTime':
                return item.entryTime;
            default:
                return '';
        }
    };

    // Sort data arrays
    const sortedRerouteAdvisories = sortData(rerouteAdvisories, rerouteSort, getRerouteValue);
    const sortedAirportDelays = sortData(airportDelays, airportSort, getAirportValue);
    const sortedFlightConstrainedAreas = sortData(flightConstrainedAreas, fcaSort, getFcaValue);
    const sortedIntersectingFlights = sortData(intersectingFlights, intersectionSort, getIntersectionValue);

    return (
        <>
            {details !== null && (
                <Dialog width={'min(600px, 40vw)'} title={details.title} onClose={() => setDetails(null)}>
                    <h5 className="pb-2">{details.header}</h5>
                    <div style={{ maxHeight: '40vh', minHeight: '40vh', overflowY: 'auto' }} className={'border-outline p-1'}>
                        <pre style={{ whiteSpace: 'pre-wrap' }} className="text-break">
                            {isVerbose && details.verboseContent ? details.verboseContent : details.content}
                        </pre>
                    </div>
                    <DialogActionsBar>
                        {details.showVerboseButton && (
                            <Button togglable={true} selected={isVerbose} type="button" onClick={() => setIsVerbose((x) => !x)}>
                                {isVerbose ? 'Short' : 'Verbose'}
                            </Button>
                        )}
                        <Button
                            svgIcon={isCopied ? CheckmarkOutline : DuplicateOutline}
                            type="button"
                            themeColor={'primary'}
                            onClick={saveToClipboard}>
                            {isCopied ? 'Copied!' : 'Copy to clipboard'}
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <Dialog width={'min(1000px, 100vw)'} height={'min(1000px, 100vh)'} title="Constraints List" onClose={props.onClose}>
                <div className="pb-2" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div
                        style={{
                            flex: 1,
                            marginBottom: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0
                        }}>
                        <div className={'d-flex justify-content-between'}>
                            Reroute Advisories
                            {lastRerouteUpdate && (
                                <div className={'secondary-text'}>Last updated at: {DateTimeFormatter.formatTime(lastRerouteUpdate)}</div>
                            )}
                        </div>
                        <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                            <table className="cobalt-table cobalt-alternate w-100">
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: -1,
                                            height: 30,
                                            backgroundColor: 'var(--alternate-color)',
                                            zIndex: 2
                                        }}>
                                        <SortableHeader
                                            column="advisoryNumber"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Adv #
                                        </SortableHeader>
                                        <SortableHeader
                                            column="type"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Type
                                        </SortableHeader>
                                        <SortableHeader
                                            column="category"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Cat
                                        </SortableHeader>
                                        <SortableHeader
                                            column="action"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Action
                                        </SortableHeader>
                                        <SortableHeader
                                            column="status"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Status
                                        </SortableHeader>
                                        <SortableHeader
                                            column="method"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Method
                                        </SortableHeader>
                                        <SortableHeader
                                            column="conformantFlights"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Conf
                                        </SortableHeader>
                                        <SortableHeader
                                            column="nonConformantFlights"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Non-conf
                                        </SortableHeader>
                                        <SortableHeader
                                            column="affectedRoutes"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Routes
                                        </SortableHeader>
                                        <SortableHeader
                                            column="reRouteName"
                                            sortState={rerouteSort}
                                            onSort={(col) => handleSort(col, rerouteSort, setRerouteSort)}>
                                            Reroute name
                                        </SortableHeader>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedRerouteAdvisories.map((x) => (
                                        <tr key={x.advisoryNumber + x.type}>
                                            <td>{x.advisoryNumber}</td>
                                            <td>{x.type}</td>
                                            <td>{x.category}</td>
                                            <td>{x.action}</td>
                                            <td>{x.status}</td>
                                            <td>{x.method}</td>
                                            <td>{x.conformantFlights}</td>
                                            <td>{x.nonConformantFlights}</td>
                                            <td>{x.affectedRoutes}</td>
                                            <td>{x.reRouteName}</td>
                                            <td>
                                                <Button
                                                    svgIcon={DataInsightsOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    title={'Analyze routes'}
                                                    onClick={() => {
                                                        handleAnalyzeRoutes(x);
                                                    }}></Button>
                                                <Button
                                                    svgIcon={InfoCircleOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    title={'Open details'}
                                                    onClick={() => {
                                                        openRerouteDetails(x);
                                                    }}></Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {sortedRerouteAdvisories.length === 0 && (
                                        <tr>
                                            <td colSpan={99} className="text-center">
                                                No items.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div
                        style={{
                            flex: 1,
                            marginBottom: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0
                        }}>
                        <div className={'d-flex justify-content-between'}>
                            Airport Delays
                            {lastAirportDelayUpdate && (
                                <div className={'secondary-text'}>
                                    Last updated at: {DateTimeFormatter.formatTime(lastAirportDelayUpdate)}
                                </div>
                            )}
                        </div>
                        <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                            <table className="cobalt-table cobalt-alternate w-100">
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: -1,
                                            height: 30,
                                            backgroundColor: 'var(--alternate-color)',
                                            zIndex: 2
                                        }}>
                                        <SortableHeader
                                            column="name"
                                            sortState={airportSort}
                                            onSort={(col) => handleSort(col, airportSort, setAirportSort)}>
                                            Name
                                        </SortableHeader>
                                        <SortableHeader
                                            column="type"
                                            sortState={airportSort}
                                            onSort={(col) => handleSort(col, airportSort, setAirportSort)}>
                                            Type
                                        </SortableHeader>
                                        <SortableHeader
                                            column="reason"
                                            sortState={airportSort}
                                            onSort={(col) => handleSort(col, airportSort, setAirportSort)}>
                                            Reason
                                        </SortableHeader>
                                        <SortableHeader
                                            column="start"
                                            sortState={airportSort}
                                            onSort={(col) => handleSort(col, airportSort, setAirportSort)}>
                                            Start
                                        </SortableHeader>
                                        <SortableHeader
                                            column="end"
                                            sortState={airportSort}
                                            onSort={(col) => handleSort(col, airportSort, setAirportSort)}>
                                            End
                                        </SortableHeader>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedAirportDelays.map((x) => (
                                        <tr key={x.name + x.start + x.end}>
                                            <td>{x.name}</td>
                                            <td>{x.type}</td>
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '300px',
                                                        overflowX: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        textWrap: 'nowrap'
                                                    }}>
                                                    {x.reason}
                                                </div>
                                            </td>
                                            <td>{DateTimeFormatter.formatDateTime(x.start)}</td>
                                            <td>{DateTimeFormatter.formatDateTime(x.end)}</td>
                                            <td>
                                                <Button
                                                    svgIcon={MapOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    disabled={
                                                        x.position[1] > 90 ||
                                                        x.position[1] < -90 ||
                                                        x.position[0] > 180 ||
                                                        x.position[0] < -180
                                                    }
                                                    title={'Go to on map'}
                                                    onClick={() => {
                                                        map?.goToCoordinates(new Coordinate(x.position[1], x.position[0]), 6);
                                                        props.onClose?.();
                                                    }}></Button>
                                                <Button
                                                    svgIcon={AirportTowerOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    title={'Airport summary'}
                                                    onClick={() => {
                                                        dispatch(toggleLeftFlyout({ type: 'airport', airportId: x.name }));
                                                        props.onClose?.();
                                                    }}></Button>
                                                <Button
                                                    svgIcon={InfoCircleOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    title={'Open details'}
                                                    onClick={() => {
                                                        openAirportDetails(x);
                                                    }}></Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {sortedAirportDelays.length === 0 && (
                                        <tr>
                                            <td colSpan={99} className="text-center">
                                                No items.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <div className={'d-flex justify-content-between'}>
                            FCAs/FEAs
                            {lastFlightConstrainedAreaUpdate && (
                                <div className={'secondary-text'}>
                                    Last updated at: {DateTimeFormatter.formatTime(lastFlightConstrainedAreaUpdate)}
                                </div>
                            )}
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            <table className="cobalt-table cobalt-alternate w-100">
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: -1,
                                            height: 30,
                                            backgroundColor: 'var(--alternate-color)',
                                            zIndex: 1
                                        }}>
                                        <SortableHeader
                                            column="name"
                                            sortState={fcaSort}
                                            onSort={(col) => handleSort(col, fcaSort, setFcaSort)}>
                                            Name
                                        </SortableHeader>
                                        <SortableHeader
                                            column="type"
                                            sortState={fcaSort}
                                            onSort={(col) => handleSort(col, fcaSort, setFcaSort)}>
                                            Type
                                        </SortableHeader>
                                        <SortableHeader
                                            column="reason"
                                            sortState={fcaSort}
                                            onSort={(col) => handleSort(col, fcaSort, setFcaSort)}>
                                            Reason
                                        </SortableHeader>
                                        <SortableHeader
                                            column="start"
                                            sortState={fcaSort}
                                            onSort={(col) => handleSort(col, fcaSort, setFcaSort)}>
                                            Start
                                        </SortableHeader>
                                        <SortableHeader
                                            column="end"
                                            sortState={fcaSort}
                                            onSort={(col) => handleSort(col, fcaSort, setFcaSort)}>
                                            End
                                        </SortableHeader>
                                        <SortableHeader
                                            column="altitude"
                                            sortState={fcaSort}
                                            onSort={(col) => handleSort(col, fcaSort, setFcaSort)}>
                                            Altitude
                                        </SortableHeader>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedFlightConstrainedAreas.map((x) => (
                                        <tr key={x.name + x.type + x.start + x.end}>
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '100px',
                                                        overflowX: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        textWrap: 'nowrap'
                                                    }}>
                                                    {x.name}
                                                </div>
                                            </td>
                                            <td>{x.type}</td>
                                            <td>{x.reason}</td>
                                            <td>{DateTimeFormatter.formatDateTime(x.start)}</td>
                                            <td>{DateTimeFormatter.formatDateTime(x.end)}</td>
                                            <td>{x.altitude ? UnitFormatter.formatAltitude(x.altitude) : ''}</td>
                                            {/*<td>{x.affectedFlights}</td>*/}
                                            <td>
                                                <Button
                                                    svgIcon={MapOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    disabled={
                                                        x.center[1] > 90 || x.center[1] < -90 || x.center[0] > 180 || x.center[0] < -180
                                                    }
                                                    title={'Go to on map'}
                                                    onClick={() => {
                                                        const [lon, lat] = x.center;
                                                        map?.goToCoordinates(new Coordinate(lat, lon), 6);
                                                        props.onClose?.();
                                                    }}></Button>
                                                <DropDownButton
                                                    className="p-0"
                                                    fillMode={'flat'}
                                                    svgIcon={MenuOverflowVerticalOutline}
                                                    onItemClick={(e) => {
                                                        switch (e.itemIndex) {
                                                            case 0: {
                                                                addDrawingObject(x);
                                                                const [lon, lat] = x.center;
                                                                map?.goToCoordinates(new Coordinate(lat, lon), 6);
                                                                props.onClose?.();
                                                                break;
                                                            }
                                                            case 1:
                                                                showIntersectingFlights(x, true);
                                                                break;
                                                            case 2:
                                                                showIntersectingFlights(x, false);
                                                                break;
                                                            case 3: {
                                                                addFPMAvoidanceArea(x);
                                                                const [lon, lat] = x.center;
                                                                map?.goToCoordinates(new Coordinate(lat, lon), 6);
                                                                props.onClose?.();
                                                                break;
                                                            }
                                                        }
                                                    }}>
                                                    <DropDownButtonItem text={'Create drawing object'} svgIcon={DrawingPolygonOutline} />
                                                    <DropDownButtonItem
                                                        text={'Show MyFleet aircraft whose routes intersect with this area'}
                                                        svgIcon={FlightListSolid}
                                                    />
                                                    <DropDownButtonItem
                                                        text={'Show all aircraft whose routes intersect with this area'}
                                                        svgIcon={FlightListOutline}
                                                    />
                                                    {integrationInfo.enabled && integrationInfo.isFPM && (
                                                        <DropDownButtonItem
                                                            disabled={!integrationInfo.isActionEnabled('canSendDrawObjToFPM')}
                                                            text={
                                                                integrationInfo.getDisabledReason('canSendDrawObjToFPM')
                                                                    ? `Create FPM avoidance area (${integrationInfo.getDisabledReason('canSendDrawObjToFPM')})`
                                                                    : 'Create FPM avoidance area'
                                                            }
                                                            svgIcon={DrawingPolygonOutline}
                                                        />
                                                    )}
                                                </DropDownButton>
                                                <Button
                                                    svgIcon={InfoCircleOutline}
                                                    fillMode={'flat'}
                                                    className="button-small"
                                                    title={'Open details'}
                                                    onClick={() => {
                                                        openFCADetails(x);
                                                    }}></Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {sortedFlightConstrainedAreas.length === 0 && (
                                        <tr>
                                            <td colSpan={99} className="text-center">
                                                No items.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Dialog>

            {intersectionName !== null && (
                <Window initialWidth={600} initialHeight={700} title="Route intersection results" onClose={() => setIntersectionName(null)}>
                    <div className="pb-2" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <div className={'d-flex justify-content-between'}>
                                {intersectionName}
                                <div className={'secondary-text'}>Plane count: {sortedIntersectingFlights.length}</div>
                            </div>
                            <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                                <table className="cobalt-table cobalt-alternate w-100">
                                    <thead>
                                        <tr
                                            style={{
                                                position: 'sticky',
                                                top: -1,
                                                height: 30,
                                                backgroundColor: 'var(--alternate-color)',
                                                zIndex: 1
                                            }}>
                                            <SortableHeader
                                                column="acid"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                ACID
                                            </SortableHeader>
                                            <SortableHeader
                                                column="origin"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                Origin
                                            </SortableHeader>
                                            <SortableHeader
                                                column="destination"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                Dest
                                            </SortableHeader>
                                            <SortableHeader
                                                column="status"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                Status
                                            </SortableHeader>
                                            <SortableHeader
                                                column="departureTime"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                Dep Time
                                            </SortableHeader>
                                            <SortableHeader
                                                column="arrivalTime"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                Arr Time
                                            </SortableHeader>
                                            <SortableHeader
                                                column="entryTime"
                                                sortState={intersectionSort}
                                                onSort={(col) => handleSort(col, intersectionSort, setIntersectionSort)}>
                                                Entry Time
                                            </SortableHeader>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedIntersectingFlights.map((x) => (
                                            <tr key={x.acid + x.origin + x.entryTime}>
                                                <td>{x.acid}</td>
                                                <td>{x.origin}</td>
                                                <td>{x.destination}</td>
                                                <td>{x.status}</td>
                                                <td>{DateTimeFormatter.formatTime(x.departureTime)}</td>
                                                <td>{DateTimeFormatter.formatTime(x.arrivalTime)}</td>
                                                <td>{DateTimeFormatter.formatTime(x.entryTime)}</td>
                                            </tr>
                                        ))}
                                        {sortedIntersectingFlights.length === 0 && intersectionComplete && (
                                            <tr>
                                                <td colSpan={99} className="text-center">
                                                    No items.
                                                </td>
                                            </tr>
                                        )}
                                        {!intersectionComplete && sortedIntersectingFlights.length === 0 && (
                                            <tr>
                                                <td colSpan={99} className="text-center">
                                                    <LoadingIndicatorCircular />
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </Window>
            )}
        </>
    );
};

export default ConstraintsList;
